import * as React from "react";
import { cn } from "@/lib/utils";

export interface FormTextareaProps extends Omit<React.ComponentProps<"textarea">, "maxLength"> {
  label?: string;
  error?: string;
  maxLength?: number;
  showCount?: boolean;
  containerClassName?: string;
  required?: boolean;
}

export const FormTextarea = React.forwardRef<HTMLTextAreaElement, FormTextareaProps>(
  (
    {
      label,
      error,
      id,
      className,
      containerClassName,
      maxLength = 1000,
      showCount = true,
      onChange,
      defaultValue,
      value,
      required,
      ...props
    },
    ref,
  ) => {
    const textareaId = id || React.useId();
    const isDisabled = props.disabled;
    const [count, setCount] = React.useState(
      typeof value === "string"
        ? value.length
        : typeof defaultValue === "string"
          ? defaultValue.length
          : 0,
    );

    return (
      <div className={cn("flex w-full flex-col gap-2", containerClassName)}>
        {label && (
          <label
            htmlFor={textareaId}
            className={cn(
              "font-sans text-[12px] leading-[16px]",
              isDisabled ? "text-text-muted" : "text-text-primary",
            )}
          >
            {label}
            {required && <span className="ml-1 text-text-primary">*</span>}
          </label>
        )}
        <div
          className={cn(
            "group flex w-full flex-col rounded-t-[16px] border-b-2 bg-surface-warm px-4 pt-2 pb-1 transition-colors",
            error
              ? "border-destructive"
              : isDisabled
                ? "border-primary-200 bg-[color-mix(in_oklab,var(--primary-100)_50%,transparent)]"
                : "border-primary-100 focus-within:border-primary-600",
          )}
        >
          <textarea
            ref={ref}
            id={textareaId}
            maxLength={maxLength}
            value={value}
            defaultValue={defaultValue}
            required={required}
            onChange={(e) => {
              setCount(e.currentTarget.value.length);
              onChange?.(e);
            }}
            className={cn(
              "min-h-[180px] w-full resize-none bg-transparent",
              "font-sans text-[16px] leading-[22px] font-light text-text-primary",
              "placeholder:text-[color-mix(in_oklab,var(--text-muted)_60%,transparent)]",
              "focus:outline-none disabled:cursor-not-allowed disabled:text-text-muted",
              className,
            )}
            {...props}
          />
          {showCount && (
            <div className="flex h-[22px] items-center justify-end">
              <span className="font-sans text-[12px] leading-[22px] font-light text-text-secondary">
                {count}/{maxLength}
              </span>
            </div>
          )}
        </div>
        {error && (
          <p className="font-sans text-[12px] leading-[16px] font-light text-destructive">
            {error}
          </p>
        )}
      </div>
    );
  },
);
FormTextarea.displayName = "FormTextarea";